import './DataAccessSearch.scss';
import { createAsyncSelectors } from '@lib/selectors';
import { DataAccessCompareOption } from './DataAccessCompareButton';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { RecentSearchResults } from './RecentSearchResults';
import { SearchOutlined } from '@ant-design/icons';
import { ShowMoreTypeLabel } from './ShowMoreTypeLabel';
import { useNavigate } from 'react-router-dom';
import { Button, Select, Space } from 'antd';
import { COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataAccessType, SEARCH_LIMIT, TYPE_SEARCH_LIMIT } from '@sonar-software/sonar-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useAsyncState } from '@hooks';
export const dataAccessSearchDataSelectors = createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.search.get);
export const DataAccessSearch = ({ defaultValue, showRecent, showCompare }) => {
    const [query, setQuery] = useState('');
    const [currentType, setCurrentType] = useState();
    const navigate = useNavigate();
    const { data, isRequested } = useAsyncState(dataAccessSearchDataSelectors);
    const getSearch = useAction(coreActions.requestGetCoreDataSecurityDataAccessSearch);
    const resetSearch = useAction(coreActions.resetGetCoreDataSecurityDataAccessSearch);
    useEffect(() => {
        if ((query === null || query === void 0 ? void 0 : query.length) >= 3) {
            getSearch({
                queryString: query,
                searchLimit: currentType ? TYPE_SEARCH_LIMIT : SEARCH_LIMIT,
                target: currentType
            });
        }
    }, [currentType, getSearch, query]);
    const handleOnSearch = useCallback((value) => {
        const cleanValue = value === null || value === void 0 ? void 0 : value.trim();
        setQuery(cleanValue);
        if (!(cleanValue === null || cleanValue === void 0 ? void 0 : cleanValue.length) || (!currentType && data !== null)) {
            setCurrentType(undefined);
            resetSearch();
        }
    }, [currentType, data, resetSearch]);
    const options = useMemo(() => {
        if (!data) {
            return [];
        }
        return Object.keys(data).map((type) => {
            var _a;
            const typeResults = (_a = data[type]) !== null && _a !== void 0 ? _a : [];
            const label = React.createElement(ShowMoreTypeLabel, { allowShowMore: currentType !== type, onClick: setCurrentType, type: type });
            return {
                label,
                options: typeResults.map((entry) => ({
                    label: entry.display,
                    type,
                    value: entry.id
                }))
            };
        });
    }, [currentType, data]);
    const handleSelect = useCallback((option) => {
        const { label, type, value } = option;
        if (type === DataAccessType.Object) {
            navigate(`${FULL_ROUTES.DATA_SECURITY_DATA_ACCESS_ACCESS_AUDIT}/${value}?objectName=${label}`);
        }
        else {
            // eslint-disable-next-line max-len
            navigate(`${FULL_ROUTES.DATA_SECURITY_DATA_ACCESS_ACCESS_EXPLAINER}/${value}?objectName=${label}&type=${type}`);
        }
    }, [navigate]);
    // The "Compare" button should not appear on the Access Explainer or Access Audit pages,
    // as those pages deal with Salesforce objects, not access objects.
    const renderOption = useCallback((option) => {
        const searchOption = option.data;
        if (!showCompare) {
            return searchOption.label;
        }
        return React.createElement(DataAccessCompareOption, { option: searchOption });
    }, [showCompare]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Space.Compact, { className: 'data-access-search', size: 'large' },
            React.createElement(Select, { allowClear: true, className: 'data-access-select', defaultValue: defaultValue, filterOption: false, labelInValue: true, listHeight: 720, maxLength: 128, notFoundContent: isRequested ? React.createElement(LoadingSpinner, { spinning: true }) : null, onSearch: handleOnSearch, onSelect: (_, option) => handleSelect(option), optionLabelProp: 'label', optionRender: renderOption, options: options, placeholder: COPY_SPECIFIC.SEARCH_FOR_DATA_ACCESS, showSearch: true, suffixIcon: null }),
            React.createElement(Button, { icon: React.createElement(SearchOutlined, null), type: 'primary' })),
        showRecent && React.createElement(RecentSearchResults, { onSelect: handleSelect })));
};
